import React, { useEffect, useRef, useState } from 'react';
import { Box, Center, Flex, Heading, Image, Link, Text, useToast } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

import { useReduxDispatch } from '../../../app/store';
import chargerImg from '../../../assets/images/evse_charger_qr_code.svg';
import { ArcButton } from '../../../common/components/ArcButton';
import { BoltLoader } from '../../../common/components/BoltLoader';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_IOS, IS_RUNNING_CYPRESS_TESTS } from '../../../common/constants';
import { trackCameraAccessOutcome, trackConnectedToChargerWifi } from '../../../utils/common/analytics';
import {
  ensureOnboardingFunnelTimerStarted,
  trackDynatraceCameraAccessOutcome,
  trackDynatraceChargerAddedSuccessfully,
  trackDynatraceConnectedToChargerWifi,
  trackDynatraceEvseVariantDetected,
  trackDynatraceEvseVariantDetectionError,
} from '../../../utils/native/dynatrace-events';
import { selectSite } from '../../site/siteSlice';
import { setEvseVariant } from '../dashboard/evChargerSlice';
import { getTestingQRCodeContent, parseQRCode } from '../ev-charger-helper';
import { DetectedEvseModel } from '../evse-variant';
import { useDebugLogs } from '../use-debug-logs';
import { isEvseApReachable } from '../wifi-configuration/wifi-connect-helper';
import { selectWifiConfigState } from '../wifi-configuration/wifiConfigSlice';
import {
  enrolEvCharger,
  resetEnrollAttemptCounter,
  resetEnrollState,
  selectDeviceSetupState,
  setDetectedEvseModel,
  setQRCodeValues,
  syncDetectedEvseModel,
} from './deviceSetupSlice';
import { waitForEvseApReady } from './evse-ap-readiness';
import { getEvseApReadinessTimingMs } from './evse-ap-readiness-settings';
import { detectEvseModelFromSoftAp, detectVirtualEvseModelFromSsid } from './evse-variant-detection';
import { useQRCodeScan } from './use-qr-code-scan';
import { useSetupBackUrl } from './use-setup-back-url';

/* istanbul ignore next -- @preserve */
export const DeviceScan = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { debugToast } = useDebugLogs();
  const { scanQRCode, showInvalidQRCodeToast } = useQRCodeScan();
  const { withSetupBackUrl } = useSetupBackUrl();
  const dispatch = useReduxDispatch();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);
  const [isSsidHidden, setIsSsidHidden] = useState(false);
  const retryConnectResolver = useRef<((shouldRetry: boolean) => void) | null>(null);
  const { isEditing } = useSelector(selectWifiConfigState);
  const { hasInitiatedEnroll } = useSelector(selectDeviceSetupState);
  const site = useSelector(selectSite);
  const isTestingBarcodeScanError = (window as Window & typeof globalThis).isTestingBarcodeScanError;
  const isTestingWifiConnectError = (window as Window & typeof globalThis).isTestingWifiConnectError;
  const isTestingVirtualDevice = (window as Window & typeof globalThis).isTestingVirtualDevice;
  const isTestingInvalidCpid = (window as Window & typeof globalThis).isTestingInvalidCpid;

  useEffect(() => {
    return () => {
      dispatch(resetEnrollState());
      // Reset here (not on mount) so retries within the same visit keep incrementing correctly;
      // leaving this screen — success or abandonment — means the next visit is a fresh attempt.
      resetEnrollAttemptCounter();
      retryConnectResolver.current?.(false);
    };
  }, [dispatch, navigate]);

  // iOS hides the SSID when Precise Location is off; if the charger didn't answer either, point users at it.
  async function checkIsSsidHidden() {
    const hidden = IS_IOS && !(await CapacitorWifiConnect.getDeviceSSID().catch(() => undefined))?.value;
    setIsSsidHidden(hidden);
    return hidden;
  }

  async function confirmRetryConnect() {
    await checkIsSsidHidden();
    return new Promise<boolean>((resolve) => {
      retryConnectResolver.current = resolve;
      setIsRetryDialogOpen(true);
    });
  }

  function handleRetryConnectResponse(shouldRetry: boolean) {
    retryConnectResolver.current?.(shouldRetry);
    retryConnectResolver.current = null;
    setIsRetryDialogOpen(false);
  }

  // Best effort: a failure leaves the variant as-is so onboarding can always continue.
  async function detectEvseVariant(preDetected?: DetectedEvseModel) {
    try {
      const detected = preDetected ?? (await detectEvseModelFromSoftAp(toast));
      debugToast('EVSE model detected', `${detected.modelNumber} -> ${detected.evseVariant}`);
      dispatch(setDetectedEvseModel(detected));
      dispatch(setEvseVariant(detected.evseVariant));
      trackDynatraceEvseVariantDetected(detected.evseVariant, detected.modelNumber, isEditing);
    } catch (error) {
      debugToast('Error detecting EVSE variant', JSON.stringify(error));
      trackDynatraceEvseVariantDetectionError(
        error instanceof Error ? error.message : JSON.stringify(error),
        isEditing
      );
    }
  }

  async function handleStartQRCodeScan() {
    // Handle QR code scan success and fail when running Cypress tests.
    const codeContent = IS_RUNNING_CYPRESS_TESTS
      ? window.isTestingQRCodeContent ??
        getTestingQRCodeContent(isTestingBarcodeScanError, isTestingVirtualDevice, isTestingInvalidCpid)
      : await scanQRCode();
    trackCameraAccessOutcome(codeContent ? 'granted' : 'denied');
    trackDynatraceCameraAccessOutcome(codeContent ? 'granted' : 'denied');

    const codeValues = parseQRCode(codeContent);
    if (!codeValues || !codeValues.cpid || !codeValues.sn || !codeValues.wifi) {
      Sentry.captureMessage('Invalid EVSE device QR code', {
        level: 'error',
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceScan',
          method: 'parseQRCode',
        },
        extra: { codeContent, codeValues: JSON.stringify(codeValues) },
      });
      showInvalidQRCodeToast();
      return;
    }

    const wifi = codeValues.wifi;

    setIsConnecting(true);
    dispatch(setQRCodeValues(codeValues));
    // Cleared before enrolment so a stale detection can't be saved against the newly added charger.
    dispatch(setDetectedEvseModel(null));

    if (!isEditing && !hasInitiatedEnroll) {
      // No-ops if trackDynatraceEvseOnboardingStarted() already started the timer (the normal
      // DeviceRegister flow); starts it fresh for entry points that skip that screen (e.g.
      // NoDevice's "Add Charger"), so elapsed_ms_since_start reflects this attempt, not a stale one.
      ensureOnboardingFunnelTimerStarted();

      const enrollPayload: { status: 'success' | 'fail'; message?: string; reason?: string } = unwrapResult(
        await dispatch(enrolEvCharger(codeValues))
      );
      if (enrollPayload.status === 'fail') {
        let title = t('Common.invalid qr code');
        let errorMessage = enrollPayload.message;

        // Handle enrollment-specific error reasons
        if (enrollPayload.reason === 'not-found') {
          errorMessage = t('Set Up Hardware.charger serial number issue');
        } else if (enrollPayload.reason === 'invalid-uuid') {
          errorMessage = t('Set Up Hardware.malformed qr code data');
        } else if (enrollPayload.reason === 'charger-id-mismatch') {
          errorMessage = t('Set Up Hardware.charger id missmatch');
        } else if (enrollPayload.reason === 'charger-already-in-use') {
          errorMessage = t('Set Up Hardware.charger is already linked');
        } else {
          // Other enrollment errors (model not supported etc.)
          title = t('Common.something went wrong');
          if (enrollPayload.message?.includes('Not support EVSE charger model')) {
            errorMessage = t('Set Up Hardware.unsupported charger');
          }
        }

        toast({
          title,
          description: errorMessage || t('Common.if this persists contact support'),
          status: 'error',
          duration: null,
          isClosable: true,
        });

        Sentry.captureMessage(errorMessage || t('Common.if this persists contact support'), {
          level: 'error',
          tags: {
            category: 'feature',
            feature: 'ev-charger',
            component: 'DeviceScan',
            method: 'parseQRCode',
          },
          extra: { cpid: codeValues.cpid, enrollPayload },
        });

        dispatch(resetEnrollState());
        setIsConnecting(false);
        return;
      }
    }

    // Virtual devices skip the physical WiFi connection and go straight to tariff setup.
    if (wifi.ssid.toLowerCase().startsWith('virtual')) {
      // This is the funnel's real completion point for virtual devices, since they never reach
      // DeviceWifiConnection/NoWifiConnection (the only other callers) - without this, the funnel
      // timer is never cleared and leaks a stale elapsed_ms_since_start into the next attempt.
      if (!isEditing) trackDynatraceChargerAddedSuccessfully();

      // Detect the virtual EVSE model from the SSID and update the backend.
      const detectedModel = detectVirtualEvseModelFromSsid(wifi.ssid);
      await detectEvseVariant(detectedModel);
      dispatch(syncDetectedEvseModel());

      const onboardingTariffPath = `/sites/${site.site_id}/account/tariff/new?direction=forward&backURL=/sites/${site.site_id}/home/device-status?direction=back`;
      navigate(onboardingTariffPath);
      setIsConnecting(false);
      return;
    }

    const connectToEvseAndContinue = async () => {
      setIsConnecting(true);

      try {
        let currentNetwork = null;
        let isSsidUnverified = false;

        if (IS_NATIVE) {
          const readinessTiming = getEvseApReadinessTimingMs();
          const readiness = await waitForEvseApReady({
            ssid: wifi.ssid,
            password: wifi.password,
            ...readinessTiming,
            onConnectFailed: confirmRetryConnect,
            isApReachableFn: IS_IOS ? isEvseApReachable : undefined,
          });

          if (!readiness.ok) {
            if (readiness.reason === 'cancelled') {
              setIsConnecting(false);
              return;
            }
            throw new Error('Timed out waiting for EV Charger Wi-Fi Access Point to become ready');
          }

          isSsidUnverified = !!readiness.ssidUnverified;
          currentNetwork = await CapacitorWifiConnect.getDeviceSSID().catch((error) => {
            if (!isSsidUnverified) throw error;
            return null;
          });
          debugToast('Current network', JSON.stringify(currentNetwork));
        }

        if (!currentNetwork?.value && !isSsidUnverified && (!IS_RUNNING_CYPRESS_TESTS || isTestingWifiConnectError)) {
          // Handle Wi-Fi connect success and fail when running Cypress tests.
          throw new Error('Could not connect to EV Charger Wi-Fi Access Point');
        }

        // Check if user cancelled or connected to wrong network.
        if (
          !IS_RUNNING_CYPRESS_TESTS &&
          currentNetwork?.value !== wifi.ssid &&
          !(isSsidUnverified && !currentNetwork?.value)
        ) {
          debugToast('Wrong network', `Connected to ${currentNetwork?.value}, expected ${wifi.ssid}`);
          throw new Error(`Connected to ${currentNetwork?.value} instead of EV Charger Wi-Fi Access Point`);
        }

        trackConnectedToChargerWifi();
        trackDynatraceConnectedToChargerWifi();

        await detectEvseVariant();
        navigate(withSetupBackUrl('../device-wifi-2ghz-info'));
      } catch (error) {
        debugToast('Error connecting to wifi', JSON.stringify(error));
        Sentry.captureException(error, {
          tags: {
            category: 'feature',
            feature: 'ev-charger',
            component: 'DeviceScan',
            method: 'connect',
          },
        });

        const isPreciseLocationHint = IS_NATIVE && (await checkIsSsidHidden());

        toast({
          title: t('Set Up Hardware.charger wifi not activated'),
          description: (
            <Text>
              {isPreciseLocationHint
                ? t('Set Up Hardware.enable precise location for charger wifi')
                : t('Set Up Hardware.error connecting to charger wifi')}{' '}
              <Link
                onClick={() => {
                  toast.closeAll();
                  navigate(withSetupBackUrl('../device-power-up-instructions'));
                }}
                textDecoration="underline"
              >
                {t('Set Up Hardware.go to power up instructions')}
              </Link>
            </Text>
          ),
          status: 'warning',
          duration: 15000,
          isClosable: true,
        });
      } finally {
        setIsConnecting(false);
      }
    };

    await connectToEvseAndContinue();
  }

  return (
    <SlidingAnimationPageBase
      title={t('Common.device setup')}
      backURL={withSetupBackUrl('../device-wait-for-yellow-led')}
      includeTopNavHamButton
      display="flex"
      flexDirection="column"
    >
      <Center flexDirection="column" px={3} textAlign="center" flex={1}>
        <Image src={chargerImg} alt="EV Charger QR Code Scan" ml={'5.5rem'} my={4} />

        <Heading mt={2} size={'lg'}>
          {t('EVSE.allow camera title')}
        </Heading>

        <Flex direction={'column'}>
          <Text mt={1}>
            <Box as={'span'}>{t('EVSE.connect charger text 1') + '\u00A0'}</Box>
            <Box as={'span'} fontWeight={600} color="#3DCD58">
              {t('EVSE.right') + '\u00A0'}
            </Box>
            <Box as={'span'}>{t('EVSE.connect charger text 2')}</Box>
          </Text>
        </Flex>

        <ArcButton
          data-testid="ev-charger-scan-device-qr"
          onClick={handleStartQRCodeScan}
          isDisabled={!IS_NATIVE && !IS_RUNNING_CYPRESS_TESTS}
          mt="auto"
          mb={8}
          w={'100%'}
          arcColor="#3DCD57"
        >
          {IS_NATIVE || IS_RUNNING_CYPRESS_TESTS
            ? t('Common.open camera')
            : t('Set Up Hardware.scanning not available')}
        </ArcButton>
      </Center>
      {isConnecting && <BoltLoader description={t('EVSE.connecting to wifi')} />}
      <AlertDialogModal
        isOpen={isRetryDialogOpen}
        onClose={() => handleRetryConnectResponse(false)}
        header={t('Set Up Hardware.charger wifi not activated')}
        subHeader={
          isSsidHidden
            ? t('Set Up Hardware.enable precise location for charger wifi')
            : t('Set Up Hardware.error reconnecting to charger wifi')
        }
        confirmButtonName={t('Common.retry')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={() => handleRetryConnectResponse(true)}
        onCancel={() => handleRetryConnectResponse(false)}
        dialogContentTestId="evse-ap-retry-dialog"
        cancelButtonTestId="evse-ap-retry-cancel-button"
        confirmButtonTestId="evse-ap-retry-confirm-button"
      />
    </SlidingAnimationPageBase>
  );
};
